import React, { useState, useRef, useEffect } from "react";
import confetti from "canvas-confetti";
import { Play, Pause, Copy, Check, Loader2 } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { RevealHeading, MagneticButton } from "./Motion";
import { Waveform, StaticWaveform } from "./Waveform";
import { ScribbleCircle, Sparkle, DashedArc, BnGlyph } from "./Decorations";
import { toast } from "sonner";
import { API_URL } from "@/lib/api";

gsap.registerPlugin(ScrollTrigger);

const SAMPLE = `বাংলাদেশের অর্থনীতি ২০২৬ সালে নতুন উচ্চতায় পৌঁছেছে। গত মাসে রপ্তানি আয় বেড়েছে ১২ শতাংশ, যা দেশের ইতিহাসে রেকর্ড। বিশেষজ্ঞরা বলছেন, পোশাক খাত এবং তথ্যপ্রযুক্তি খাত মিলিয়ে এই প্রবৃদ্ধি সম্ভব হয়েছে।`;

const DEMO_AUDIO_URL = `/demos/demo_noor.wav?v=2`;

const EMBED_CODE = `<script src="https://audo.dev/embed/v1.js" data-article="a1b2c3"></script>`;

export const LiveDemo = () => {
    const [state, setState] = useState("idle"); // idle | ready
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [copied, setCopied] = useState(false);
    const [typedCode, setTypedCode] = useState("");
    const codeRef = useRef(null);
    const playerRef = useRef(null);
    const audioRef = useRef(null);

    // typewriter for embed code when ready
    useEffect(() => {
        if (state !== "ready") { setTypedCode(""); return; }
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setTypedCode(EMBED_CODE); return;
        }
        let i = 0;
        const t = setInterval(() => {
            i++;
            setTypedCode(EMBED_CODE.slice(0, i));
            if (i >= EMBED_CODE.length) clearInterval(t);
        }, 18);
        return () => clearInterval(t);
    }, [state]);

    // animate player on reveal
    useEffect(() => {
        if (state === "ready" && playerRef.current) {
            gsap.from(playerRef.current, { y: 30, opacity: 0, duration: 0.7, ease: "power3.out" });
        }
    }, [state]);

    // track playback progress
    useEffect(() => {
        if (!playing || !audioRef.current) return;
        const id = setInterval(() => {
            const a = audioRef.current;
            if (!a || !a.duration) return;
            setProgress((a.currentTime / a.duration) * 100);
        }, 200);
        return () => clearInterval(id);
    }, [playing]);

    const handleGenerate = () => {
        setState("ready");
    };

    const handlePlayPause = async () => {
        if (playing) {
            audioRef.current?.pause();
            setPlaying(false);
            return;
        }
        // Create or reuse audio element
        if (!audioRef.current) {
            const audio = new Audio(DEMO_AUDIO_URL);
            audioRef.current = audio;
            audio.onended = () => { setPlaying(false); setProgress(0); };
            audio.onerror = () => {
                setPlaying(false);
                toast.error("Could not load demo audio.");
            };
        }
        try {
            await audioRef.current.play();
            setPlaying(true);
        } catch {
            toast.error("Playback failed. Please try again.");
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(EMBED_CODE);
        } catch {}
        setCopied(true);
        toast.success("Embed code copied", { description: "Paste it into any HTML page." });
        const rect = codeRef.current?.getBoundingClientRect();
        const x = rect ? (rect.left + rect.width / 2) / window.innerWidth : 0.5;
        const y = rect ? (rect.top + 40) / window.innerHeight : 0.5;
        confetti({
            particleCount: 80, spread: 70, origin: { x, y },
            colors: ["#FF5B3E", "#3D5BFF", "#2EE8B5", "#FFD23F", "#FF4FA3", "#7B3FE4"],
            scalar: 0.9,
        });
        setTimeout(() => setCopied(false), 2200);
    };

    return (
        <section id="demo" className="relative py-24 lg:py-36 overflow-hidden">
            <ScribbleCircle color="#FF4FA3" className="absolute top-16 left-[3%] w-28 h-28 opacity-50 animate-float-slow"/>
            <DashedArc color="#3D5BFF" className="absolute top-[40%] right-[2%] w-32 h-32 opacity-50 animate-spin-slow"/>
            <BnGlyph glyph="শোনো" className="absolute right-[-20px] bottom-10 text-[160px] lg:text-[220px] leading-none font-bold opacity-[0.05] select-none" color="#FF5B3E"/>

            <div className="relative mx-auto max-w-[1100px] px-5 sm:px-8">
                <div className="text-center mb-12 lg:mb-16">
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-5">Live demo</p>
                    <RevealHeading as="h2" className="h-display text-[44px] sm:text-[68px] lg:text-[96px] text-ink">
                        Try it right now.
                    </RevealHeading>
                    <p className="mt-6 max-w-[560px] mx-auto text-[16px] sm:text-[18px] text-ink-muted">
                        Hear the future. Hit generate and listen to AI-powered Bengali TTS.
                    </p>
                </div>

                <div className="bg-white rounded-[36px] border border-cream-300 shadow-soft-lg p-5 sm:p-8 lg:p-10 relative">
                    <Sparkle color="#FFD23F" className="absolute -top-3 -right-3 w-9 h-9 animate-float-fast"/>

                    <label className="block text-[12px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-3">
                        Bengali article
                    </label>

                    {/* Read-only sample text */}
                    <div
                        data-testid="demo-textarea"
                        className="bn w-full bg-cream-100 border border-cream-300 rounded-2xl p-5 text-[16px] leading-[1.6] text-ink select-none"
                    >
                        {SAMPLE}
                    </div>

                    <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <MagneticButton
                            data-testid="demo-generate"
                            onClick={handleGenerate}
                            strength={0.2}
                            className="bg-ink text-cream-100 rounded-full px-6 py-3.5 text-[14.5px] font-semibold inline-flex items-center justify-center gap-2 hover:shadow-pill transition-all"
                        >
                            Generate Audio →
                        </MagneticButton>
                        <span className="text-[13px] text-ink-muted">Free for 20 articles/month after sign-up.</span>
                    </div>

                    {/* Audio player reveal */}
                    {state === "ready" && (
                        <div ref={playerRef} className="mt-8" data-testid="demo-player">
                            <div className="bg-cream-100 rounded-3xl p-5 sm:p-6 border border-cream-300">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-mint">● Ready</span>
                                    <span className="text-[11px] text-ink-muted ml-auto font-mono">voice: noor · 1.0×</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={handlePlayPause}
                                        data-testid="demo-play"
                                        className="w-14 h-14 rounded-full bg-coral hover:bg-coral-dark text-white flex items-center justify-center shadow-coral hover:scale-105 transition-all flex-shrink-0"
                                        aria-label={playing ? "Pause" : "Play"}
                                    >
                                        {playing ? <Pause size={20} fill="currentColor"/> : <Play size={20} className="ml-0.5" fill="currentColor"/>}
                                    </button>
                                    <div className="flex-1 relative h-12 flex items-center">
                                        <div className="absolute inset-0 flex items-center pointer-events-none">
                                            <Waveform bars={56} color={playing ? "#FF5B3E" : "#5C5C5C"} height={48} playing={playing} intensity={1.2}/>
                                        </div>
                                    </div>
                                    <span className="font-mono text-[12px] text-ink-muted whitespace-nowrap">
                                        {Math.floor(progress * 0.42 / 60).toString().padStart(2, "0")}:{Math.floor(progress * 0.42 % 60).toString().padStart(2, "0")} / 0:42
                                    </span>
                                </div>
                                <div className="mt-3 h-1 bg-cream-300 rounded-full overflow-hidden">
                                    <div className="h-full bg-coral rounded-full transition-all" style={{ width: `${progress}%` }}/>
                                </div>
                            </div>

                            {/* Embed code */}
                            <div className="mt-6">
                                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-3">
                                    Embed it anywhere
                                </p>
                                <div ref={codeRef} className="relative code-block p-5 sm:p-6 pr-28 overflow-x-auto">
                                    <pre className="text-[13px] sm:text-[14px] whitespace-pre-wrap break-all">
                                        <code>{typedCode}<span className="inline-block w-2 h-4 bg-coral animate-pulse ml-0.5 align-middle"></span></code>
                                    </pre>
                                    <button
                                        data-testid="demo-copy"
                                        onClick={handleCopy}
                                        className={`absolute top-4 right-4 px-3.5 py-2 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 transition-all ${copied ? "bg-mint text-ink" : "bg-cream-100 text-ink hover:bg-coral hover:text-white"}`}
                                    >
                                        {copied ? <><Check size={13}/> Copied</> : <><Copy size={13}/> Copy</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

